// Geo-aware currency detection for the marketing surfaces (landing +
// pricing). Four layers, fastest first:
//
//   1) `?cc=US` / `?cc=CA` URL override → for QA and support links. Wins
//      over everything, persists to cache.
//   2) localStorage cache (7-day TTL) → 0 ms, instant paint.
//   3) `Intl` timezone heuristic → 0 ms fallback. Deliberately biased
//      TOWARDS CAD (our higher, safer default) when the timezone is
//      shared between US and Canada (Pacific/Mountain/Central).
//   4) Server-side geo detection at `/api/geo/detect` → ~150–400 ms,
//      ground truth. Same-origin so no CORS, no ad-block interference.
//
// The Stripe checkout step is unaffected — Stripe always presents the
// currency of the customer's card BIN at billing time. This module drives
// only the marketing-copy prices the visitor sees BEFORE clicking
// "Get started" so the sticker they saw matches the sticker they pay.
import { useEffect, useState } from "react";
import { api } from "./api";

const CACHE_KEY = "hg_currency_v2";
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// ONLY unambiguously-US timezones. Pacific/Mountain/Central are shared
// with Canada so we default those to CAD until the server confirms US.
const UNAMBIGUOUS_US_TIMEZONES = new Set([
    "America/New_York",
    "America/Detroit",
    "America/Indiana/Indianapolis",
    "America/Kentucky/Louisville",
    "America/Chicago",
    "America/Phoenix",
    "America/Anchorage",
    "America/Adak",
    "America/Puerto_Rico",
    "Pacific/Honolulu",
]);

const VALID = new Set(["USD", "CAD"]);

function readCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!VALID.has(parsed?.currency)) return null;
        if (!parsed?.at) return null;
        if (Date.now() - parsed.at > CACHE_TTL_MS) return null;
        return parsed.currency;
    } catch (_) { return null; }
}

function writeCache(currency) {
    if (!VALID.has(currency)) return;
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ currency, at: Date.now() }));
    } catch (_) { /* quota / private mode — silent */ }
}

function readOverrideFromUrl() {
    if (typeof window === "undefined") return null;
    try {
        const params = new URLSearchParams(window.location.search);
        const cc = (params.get("cc") || "").toUpperCase();
        if (cc === "US" || cc === "USD") return "USD";
        if (cc === "CA" || cc === "CAD") return "CAD";
    } catch (_) { /* ignore */ }
    return null;
}

// Instant guess for the first paint. When the timezone is ambiguous
// (shared US/CA), defaults to CAD — our safer, higher default. The
// server response will correct it within a second if the visitor is
// actually in the US.
function guessFromTimezone() {
    try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (UNAMBIGUOUS_US_TIMEZONES.has(tz)) return "USD";
        return "CAD";
    } catch (_) {
        return "CAD";
    }
}

async function fetchServerGeo() {
    try {
        const r = await api.get("/geo/detect", { timeout: 4000 });
        const cur = r?.data?.currency;
        return VALID.has(cur) ? cur : null;
    } catch (_) {
        return null;
    }
}

/**
 * React hook: returns the detected currency string `"USD" | "CAD"`.
 * Runs URL-override → cache → timezone guess → server geo, in that
 * order. Back-compat with earlier v1 signature (`const cur = useCurrency()`).
 */
export function useCurrency() {
    const [currency, setCurrencyState] = useState(() => {
        // URL override wins and immediately persists to cache so subsequent
        // pages of the same session don't need to re-detect.
        const urlOverride = readOverrideFromUrl();
        if (urlOverride) {
            writeCache(urlOverride);
            return urlOverride;
        }
        return readCache() || guessFromTimezone();
    });

    useEffect(() => {
        // URL override was already handled in the initializer. If cache is
        // fresh, trust it — no server round-trip needed.
        if (readOverrideFromUrl()) return;
        if (readCache()) return;

        let cancelled = false;
        (async () => {
            const detected = await fetchServerGeo();
            if (cancelled || !detected) return;
            writeCache(detected);
            setCurrencyState(detected);
        })();
        return () => { cancelled = true; };
    }, []);

    // Listen for manual overrides fired from anywhere (Pricing toggle,
    // dev tooling). This is a lightweight in-memory pub/sub so we don't
    // pull in a full state library.
    useEffect(() => {
        const handler = (e) => {
            const next = e?.detail?.currency;
            if (VALID.has(next)) setCurrencyState(next);
        };
        window.addEventListener("hg:currency-changed", handler);
        return () => window.removeEventListener("hg:currency-changed", handler);
    }, []);

    return currency;
}

/**
 * Imperative currency setter. Call from anywhere (e.g. a "USD/CAD"
 * toggle on the Pricing page). Persists to cache and fires a
 * `hg:currency-changed` event so every `useCurrency()` subscriber
 * re-renders with the new value.
 */
export function setCurrencyOverride(next) {
    if (!VALID.has(next)) return;
    writeCache(next);
    if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("hg:currency-changed", { detail: { currency: next } }));
    }
}

// Pricing source-of-truth. Single place to change marketing amounts so
// Landing + Pricing + any future page stay aligned. These numbers MUST
// match the `currency_options` configured on the Stripe Prices, otherwise
// the user gets sticker shock at checkout.
export const PLAN_AMOUNTS = {
    monthly:   { CAD: 34.99, USD: 24.99 },
    quarterly: { CAD: 85,    USD: 59 },
    annual:    { CAD: 279,   USD: 199 },
};

const SYMBOLS = { CAD: "$", USD: "$" };

export function formatMoney(amount, currency) {
    const sym = SYMBOLS[currency] || "$";
    const isWhole = Math.floor(amount) === amount;
    return `${sym}${isWhole ? amount : amount.toFixed(2)}`;
}

export function perDayLabel(planKey, currency) {
    const amount = PLAN_AMOUNTS[planKey][currency];
    const days = planKey === "monthly" ? 30 : planKey === "quarterly" ? 90 : 365;
    const perDay = amount / days;
    return `${SYMBOLS[currency]}${perDay.toFixed(2)}/day`;
}
